import { Box, Typography, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useTranslation } from 'react-i18next';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { tokens } from '../../../global/theme/tokens';
import { secondsToHours } from './workTimeReportUtils';

const WorkTimeReportDonut = ({ projects }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const customerProjects = (projects || [])
    .filter((p) => !p.project.isInternal && (p.workedTimeInSeconds || 0) > 0)
    .sort((a, b) => (b.workedTimeInSeconds || 0) - (a.workedTimeInSeconds || 0));

  if (customerProjects.length < 2) return null;

  const palette = Object.values(colors.colorPool || {});
  const totalSeconds = customerProjects.reduce(
    (acc, p) => acc + (p.workedTimeInSeconds || 0),
    0,
  );

  const series = customerProjects.map((p, idx) => ({
    id: p.project.id,
    value: p.workedTimeInSeconds,
    label: p.project.name,
    color: palette[idx % palette.length],
  }));

  return (
    <Box
      className="flex flex-col md:flex-row items-center gap-4 p-3 rounded-lg"
      sx={{ bgcolor: colors.card }}
    >
      <Box sx={{ position: 'relative', width: 180, height: 180 }}>
        <PieChart
          series={[
            {
              data: series,
              innerRadius: 50,
              outerRadius: 85,
              paddingAngle: 2,
              cornerRadius: 4,
            },
          ]}
          width={180}
          height={180}
          slotProps={{ legend: { hidden: true } }}
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography variant="h5" sx={{ lineHeight: 1.1 }}>
            {secondsToHours(totalSeconds, 0)} h
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {t('Customer hours')}
          </Typography>
        </Box>
      </Box>

      <Box className="flex flex-col gap-1 flex-1 w-full">
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {t('Customer project distribution')}
        </Typography>
        {customerProjects.map((p, idx) => (
          <Box key={p.project.id} className="flex items-center gap-2">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                bgcolor: palette[idx % palette.length],
                flexShrink: 0,
              }}
            />
            {p.project.image ? (
              <img
                src={p.project.image.temporaryUrl}
                alt=""
                className="w-5 h-5 object-contain rounded"
              />
            ) : (
              <ExoAvatar
                name={p.project.name}
                size="20px"
                type="rounded"
              />
            )}
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {p.project.name}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {secondsToHours(p.workedTimeInSeconds)} h
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default WorkTimeReportDonut;
